import { useRef, useState, useEffect, type ReactNode } from "react";
import Heading from "../blocks/Heading";
import Button from "../blocks/Button";
import Snackbar from "../blocks/Snackbar";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

interface VerificationCodeInputProps {
  email?: string;
  heading: string;
  description: ReactNode;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  isLoading: boolean;
  showResendButton?: boolean;
}

const VerificationCodeInput = ({
  heading,
  description,
  onVerify,
  onResend,
  isLoading,
  showResendButton = true,
}: VerificationCodeInputProps) => {
  const [code, setCode] = useState<string>("".padEnd(CODE_LENGTH, " "));
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // RESEND COOLDOWN TIMER
  // ============================================================================
  const formatCooldown = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [resendCooldown]);

  const handleResendCode = async () => {
    setError(null);
    setIsResending(true);

    try {
      await onResend();
      setCode("".padEnd(CODE_LENGTH, " ")); // Clear code on resend
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      // Success message handled by parent if needed
    } catch (err) {
      const authError = err as any;
      let errorMessage = "Failed to resend code";

      if (authError?.name === "LimitExceededException") {
        errorMessage = "Too many resend attempts. Please try again later.";
      } else if (authError?.name === "ThrottlingException") {
        errorMessage = "Please wait before requesting another code.";
      } else if (authError?.message) {
        errorMessage = authError.message;
      }

      setError(errorMessage);
      console.error("Resend code error:", authError);
    } finally {
      setIsResending(false);
    }
  };

  // ============================================================================
  // CODE INPUT HANDLERS
  // ============================================================================

  const handleChange = (index: number, value: string) => {
    // Only digits
    const digit = value.replace(/\D/g, "");
    if (!digit) return;

    const chars = code.split("");
    chars[index] = digit;
    const nextCode = chars.join("");
    setCode(nextCode);
    setError(null);

    // Move focus to next input
    if (index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const chars = code.split("");
      if (chars[index] !== " ") {
        // Clear current
        chars[index] = " ";
        setCode(chars.join(""));
      } else if (index > 0) {
        // Go back and clear previous
        inputsRef.current[index - 1]?.focus();
        const charsPrev = code.split("");
        charsPrev[index - 1] = " ";
        setCode(charsPrev.join(""));
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    const chars = code.split("");
    for (let i = 0; i < CODE_LENGTH; i++) {
      chars[i] = pasted[i] ?? " ";
    }
    setCode(chars.join(""));

    // Focus last filled or last input
    const lastIndex = Math.min(pasted.length - 1, CODE_LENGTH - 1);
    inputsRef.current[lastIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = code.replace(/ /g, "");
    if (trimmed.length !== CODE_LENGTH) {
      setError("Please enter the full 6-digit code");
      return;
    }

    setIsVerifying(true);

    try {
      await onVerify(trimmed);
    } catch (err) {
      const authError = err as any;
      let errorMessage = "Code verification failed";

      if (authError?.name === "CodeMismatchException") {
        errorMessage = "Invalid verification code. Please try again.";
      } else if (authError?.name === "ExpiredCodeException") {
        errorMessage = "Verification code expired. Please request a new one.";
      } else if (authError?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (authError?.message) {
        errorMessage = authError.message;
      }

      setError(errorMessage);
      console.error("Verify code error:", authError);
    } finally {
      setIsVerifying(false);
    }
  };

  const digits = code.split("");
  const codeComplete = code.replace(/ /g, "").length === CODE_LENGTH;

  return (
    <div className="verification">
      <Heading heading={heading} text={description} />

      {error && (
        <Snackbar text={error} cleanErrorTimeout={() => setError(null)} />
      )}

      <form onSubmit={handleSubmit} className="verification__bottom" noValidate>
        <div className="verification__code-wrapper">
          {digits.map((char, index) => (
            <input
              key={index}
              ref={(el) => {
                if (el) {
                  inputsRef.current[index] = el;
                }
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              placeholder="X"
              className="verification__code-input"
              value={char === " " ? "" : char}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={isLoading || isVerifying}
              aria-label={`Code digit ${index + 1}`}
            />
          ))}
        </div>
        <div className="verification__box">
          <div className="verification__actions">
            <Button
              disabled={isLoading || isVerifying || !codeComplete}
              text={isVerifying ? "Verifying..." : "Verify code"}
            />
            {showResendButton && (
              <button
                className="button is-text"
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || isResending || isLoading}
                aria-label="Resend verification code"
              >
                {isResending ? "Resending..." : "Resend code"}
              </button>
            )}
          </div>
          <div className="verification__resend">
            {resendCooldown > 0 && (
              <span className="verification__cooldown">
                You can resend the code in {formatCooldown(resendCooldown)}
              </span>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default VerificationCodeInput;
